"use client";

import React, { useEffect, useState } from "react";
import EmblaCarousel from "../EmblaCarousel/EmblaCarousel";
import type { EmblaOptionsType } from "embla-carousel";
import Image from "next/image";
import { Check } from "lucide-react";

type SlideType = {
  logo?: string | string[];
  type: "image" | "video";
  src: string;
  alt?: string;
  headline: string;
  description: string;
  bullets: string[];
  cta: string;
  to: string;
};

const Slides: SlideType[] = [
  {
    type: "video",
    src: "/Hauptseite.mp4",
    logo: ["./CTXr.svg", "./modeprofi.svg", "./officefortrade.svg"],
    alt: "Vorschau auf unsere Softwarelösungen",
    headline: "Entdecke unsere Lösungen",
    description: "Schau dir an, wie unsere Software deinen Alltag vereinfacht.",
    bullets: [
      "Schneller Überblick über alle Features",
      "Praxisnahe Einblicke in den Einsatz",
      "Alles auf einen Blick",
    ],
    cta: "Mehr erfahren",
    to: "/#kassensystem-sdi",
  },
  {
    type: "image",
    src: "/Bctx.webp",
    logo: "./CTXr.svg",
    alt: "Kassensystem im Schuhgeschäft",
    headline: "CASHTEX Profi für Einzelhandel und Dienstleister",
    description:
      "Schneller kassieren, sauber verwalten und klare Abläufe – passend für deinen täglichen Betrieb.",
    bullets: [
      "Einfache Bedienung im Alltag",
      "Schnelle Abläufe an der Kasse",
      "Reibungslose Verwaltung von Artikeln & Kunden",
    ],
    cta: "Mehr erfahren",
    to: "/kassensysteme/textil",
  },
  {
    type: "image",
    src: "/index/products/ModeProfiStartseite.webp",
    logo: "./modeprofi.svg",
    alt: "Regal mit Kleidung",
    headline: "ModeProfi – Warenwirtschaft für die Textilbranche",
    description:
      "Bestände, Artikelvarianten und Prozesse im Griff – vom Einkauf bis zum Verkauf.",
    bullets: [
      "Artikel & Varianten übersichtlich",
      "Bestände und Nachbestellung im Blick",
      "Effiziente Abläufe für Teams",
    ],
    cta: "Mehr erfahren",
    to: "/modeprofi",
  },
  {
    type: "image",
    src: "/index/products/Messe.webp",
    logo: "./officefortrade.svg",
    alt: "Messestand mit Büchern und Menschen",
    headline: "Office4Trade – Mobile Auftragsabwicklung",
    description:
      "Aufträge direkt beim Kunden erfassen und sicher in die Prozesse integrieren.",
    bullets: [
      "Mobile Auftragserfassung",
      "Weniger Fehler durch klare Workflows",
      "Schneller Abschluss beim Kunden",
    ],
    cta: "Mehr erfahren",
    to: "/office4trade",
  },
];

const options: EmblaOptionsType = {
  loop: true,
  align: "start",
};

const userOptions = {
  playOnInit: true,
  delay: 60000,
  stopOnMouseEnter: true,
  stopOnInteraction: false,
};

const slides: React.ReactElement[] = Slides.map(
  (slide: SlideType, idx: number) => (
    <div key={idx} className="w-full px-0 sm:px-2 h-full flex">
      <div
        className="
          mx-auto w-full h-full
          overflow-hidden
          rounded-3xl
          bg-white
          ring-1 ring-black/10
          flex flex-col
        "
      >
        <div className="flex flex-col lg:flex-row items-stretch h-full">
          {/* LINKS: Textbereich */}
          <div className="w-full lg:w-[46%] p-4 sm:p-10 lg:p-14 flex flex-col h-full">
            {slide.logo && (
              <div className="flex items-center gap-3 mb-2">
                {(Array.isArray(slide.logo) ? slide.logo : [slide.logo]).map(
                  (logo, i) => (
                    <Image
                      key={i}
                      src={logo}
                      alt="Produktlogo"
                      width={60}
                      height={22}
                      className="object-contain h-5.8"
                    />
                  ),
                )}
              </div>
            )}

            <div className="grow flex flex-col">
              <h3 className="text-lg sm:text-2xl lg:text-3xl font-semibold text-gray-900 leading-tight">
                {slide.headline}
              </h3>

              <p className="mt-2 text-gray-600 text-sm sm:text-lg leading-relaxed max-w-xl font-normal">
                {slide.description}
              </p>

              <ul className="mt-3 space-y-1 text-sm sm:text-base text-gray-700">
                {slide.bullets.map((b, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 leading-relaxed"
                  >
                    <Check className="mt-0.5 h-3.5 w-3.5 text-brand-primary flex-none" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Der Button-Bereich */}
            <div className="mt-4 flex justify-center lg:justify-start">
              <a
                href={slide.to}
                className="
                inline-flex items-center justify-center
                text-center
                rounded-full
                px-5 py-3 lg:px-8 lg:py-4
                text-sm font-normal
                text-white
                bg-brand-primary
                transition-all duration-300 ease-out
                hover:-translate-y-1 hover:shadow-lg"
              >
                {slide.cta}
              </a>
            </div>
          </div>

          {/* RECHTS: Bild oder Video */}
          <div className="w-full lg:w-[54%] relative flex items-center justify-center bg-gray-50 min-h-62.5 lg:min-h-full overflow-hidden">
            {slide.type === "video" ? (
              <video
                className="absolute inset-0 h-full w-full object-cover"
                controls
                playsInline
              >
                <source src={slide.src} type="video/mp4" />
              </video>
            ) : (
              <div className="absolute inset-0 w-full h-full">
                <Image
                  src={slide.src}
                  alt={slide.alt ?? ""}
                  fill
                  priority={idx === 0}
                  quality={90}
                  sizes="(max-width: 1024px) 100vw, 54vw"
                  className="object-cover"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  ),
);

export default function ProduktSliderClean() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return <div className="h-75" />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 pt-4">
      <EmblaCarousel
        className="products"
        options={options}
        userOptions={userOptions}
        slides={slides}
        arrows={true}
        dots={true}
      />
    </div>
  );
}